/**
 * Faktury z KSeF (nabywca) - usluga API panelu admina (backend/api/ksef_otrzymane.py, @require_admin).
 * KONTRAKT = backend (zrodlo prawdy); ksztalty ponizej sa skopiowane z docstringa api/ksef_otrzymane.py.
 *
 * Odpowiedzi ida przez success_response {success, data, message} - funkcje zwracaja odpakowane `data`.
 * Bledy: interceptor z api.js rzuca Error z `status`, `errorCode` (error_code) i `data` (CALE cialo odpowiedzi:
 * {success:false, error, komunikat, message, status_code, error_code, ...pola pomocnicze}). Pola pomocnicze
 * (brakujace/dopasowane przy BRAK_POKRYCIA, istniejaca przy FAKTURA_ISTNIEJE) siedza wprost w err.data.
 */
import api, { apiLong } from './api';

const odpakuj = (response) => response?.data?.data ?? response?.data;

export const ksefOtrzymaneService = {
  /**
   * GET /ksef/otrzymane?status&nip&od&do&rodzaj&szukaj&strona&na_strone
   * -> {wiersze: [wiersz], razem, strona, na_strone, stron, filtry}
   * wiersz = {id, ksef_numer, numer_faktury, rodzaj, korekta, data_wystawienia, data_nadania_ksef, data_trwalego_zapisu,
   *   tryb, sprzedawca_nip, sprzedawca_nazwa, sfd, nabywca_nip, netto, vat, brutto, waluta, form_code, schema_version,
   *   ksef_numer_korygowanej, numer_korygowanej, ma_xml, xml_pobrano, xml_blad, status, faktura_zakupowa_id, koszt_id,
   *   kontrola_zrodlo_id, dopasowanie{faktura|null, kwota_zgodna, kandydaci[], kontrola|null, plik_podpiety, sugestia, korygowana?},
   *   sugestia ('zakup'|'koszt'|'korekta'|null), powod_pominiecia, pobrano_at, updated_at, mozna_dodac, mozna_pominac}
   * status: nowa|dopasowana|dodana|koszt|pominieta|blad; rodzaj: VAT|KOR|ZAL|ROZ|UPR|KOR_ZAL|KOR_ROZ|korekty.
   */
  async lista(params = {}) {
    const response = await api.get('/ksef/otrzymane', { params });
    return odpakuj(response);
  },

  /**
   * GET /ksef/otrzymane/podsumowanie (bez parametrow - liczby za caly czas)
   * -> {razem, statusy{nowa,dopasowana,dodana,koszt,pominieta,blad}, rodzaje{VAT:{liczba,brutto},...},
   *     miesiace[{miesiac,liczba,brutto,nowe}], sprzedawcy[{nip,nazwa,liczba,brutto,nowe,sfd}], xml_do_pobrania, kursor,
   *     ostatnia_synchronizacja: stan|null, sync_w_toku_id: int|null,
   *     ustawienia{enabled,pobieranie_zakupow,environment,nip,ma_token,od_domyslne}}
   */
  async podsumowanie() {
    const response = await api.get('/ksef/otrzymane/podsumowanie');
    return odpakuj(response);
  },

  /**
   * POST /ksef/otrzymane/sync {tryb: 'biezace'|'pelna', od?: 'RRRR-MM-DD', do?} -> 202 {sync_id, status:'w_toku', tryb, od, do}
   * 409 SYNC_W_TOKU, 400 KSEF_BRAK_KONFIGURACJI, 502 KSEF_AUTORYZACJA / KSEF_API.
   */
  async sync(tryb = 'biezace', od = null, do_ = null) {
    const body = { tryb };
    if (od) body.od = od;
    if (do_) body.do = do_;
    const response = await apiLong.post('/ksef/otrzymane/sync', body);
    return odpakuj(response);
  },

  /**
   * GET /ksef/otrzymane/sync/status/<id> -> {sync_id, status:'w_toku'|'ok'|'blad', tryb, od_daty, rozpoczeto, zakonczono, stron,
   *   pobrano, nowych, zmienionych, xml_do_pobrania, xml_pobrane, xml_bledy, dopasowane, dopasowanie_bledy, komunikat, uruchomil, czas_s}
   */
  async statusSync(syncId) {
    const response = await api.get(`/ksef/otrzymane/sync/status/${syncId}`, { silent: true });
    return odpakuj(response);
  },

  /**
   * GET /ksef/otrzymane/<id> -> wiersz + {metadane, faktura_xml (invoice z parse_fa3_invoice: numer_faktury, data_faktury,
   *   data_dostawy, data_platnosci, sposob_platnosci, dostawca_*, nabywca_*, suma_*, waluta, rodzaj_faktury, korekta{...}) | null,
   *   pozycje[{lp, nazwa_produktu, kod_produktu, ean, jednostka, ilosc, cena_netto, stawka_vat, kwota_vat, wartosc_netto,
   *   wartosc_brutto, produkt{id,nazwa,ean}|null, dopasowanie 'ean'|'kod'|'nazwa'|null}], brakujace[{kod_sfd,nazwa,ilosc,ean}],
   *   pokrycie{dopasowane,wszystkie,pelne}, xml_parse_blad, faktura_w_systemie{id,numer,data_faktury,brutto,sklep_id,sklep,typ,kto}|null,
   *   sklepy[{id,nazwa}],
   *   platnosc{ma_platnosc, forma_platnosci_kod, forma_platnosci, platnosc_inna, opis_platnosci, termin_platnosci, terminy[{termin,opis}],
   *     zaplacono, data_zaplaty, zaplata_czesciowa[{kwota,data}], rachunki[{nr,nr_formatowany,swift,bank,opis,wlasny_banku,faktora}],
   *     rachunek_nr, rachunek_bank, rachunek_formatowany, skonto, stan 'zaplacona'|'czesciowa'|'po_terminie'|'do_zaplaty'|'nieznany'},
   *   szczegoly_xml{naglowek, sprzedawca{nip,nazwa,adres_tekst,email,telefon}, nabywca, podmioty3[], faktura, podsumowanie_vat[],
   *     adnotacje{lista[]}, dodatkowy_opis[], warunki_transakcji{umowy,zamowienia,transport...}, rozliczenie, stopka, pozycje[], korekta}|null,
   *   szczegoly_blad}; mozna_dodac = regula statusu I pelne pokrycie. (migracja 057, utils/ksef_fa_parser.py)
   */
  async szczegoly(id) {
    const response = await api.get(`/ksef/otrzymane/${id}`);
    return odpakuj(response);
  },

  /** GET /ksef/otrzymane/<id>/xml -> Blob (plik XML FA(3)); 404 XML_NIEPOBRANY. */
  async pobierzXml(id) {
    const response = await api.get(`/ksef/otrzymane/${id}/xml`, { responseType: 'blob' });
    return response?.data;
  },

  /**
   * POST /ksef/otrzymane/<id>/dodaj-fakture {location_id} -> {faktura_id, numer, ksef_numer, pozycji, location_id, nauczono}
   * 400 BRAK_POKRYCIA (err.data.brakujace [{kod_sfd,nazwa,ilosc,ean}], err.data.dopasowane), 400 ZLY_STATUS (korekta / juz dodana),
   * 404 XML_NIEPOBRANY, 409 FAKTURA_ISTNIEJE (err.data.istniejaca {id,numer,sklep,...}) | JUZ_W_SYSTEMIE.
   */
  async dodajFakture(id, locationId) {
    const response = await apiLong.post(`/ksef/otrzymane/${id}/dodaj-fakture`, { location_id: locationId });
    return odpakuj(response);
  },

  /** POST /ksef/otrzymane/<id>/pomin {powod} -> wiersz; 400 ZLY_STATUS (dodana / koszt). */
  async pomin(id, powod = '') {
    const response = await api.post(`/ksef/otrzymane/${id}/pomin`, { powod });
    return odpakuj(response);
  },

  /** POST /ksef/otrzymane/<id>/przywroc -> wiersz (po ponownym dopasowaniu); 400 ZLY_STATUS (nie pominieta). */
  async przywroc(id) {
    const response = await api.post(`/ksef/otrzymane/${id}/przywroc`);
    return odpakuj(response);
  },

  /** POST /ksef/otrzymane/<id>/dopasuj -> {id, status, faktura_zakupowa_id, kontrola_zrodlo_id, dopasowanie} */
  async dopasuj(id) {
    const response = await api.post(`/ksef/otrzymane/${id}/dopasuj`);
    return odpakuj(response);
  },
};

export default ksefOtrzymaneService;
